import { useState, useEffect, useCallback, useRef } from 'react';
import { Socket } from 'socket.io-client';
import { toast } from 'sonner';

import { videoConnectivityAPI } from '../../api/connectivityApis/video-connectivity.api';
import { astrologyAPI } from '../../api/connectivityApis/astrology.api';
import { useSessionTimer } from './useSessionTimer';
import { connectivityAPI } from '../../api/connectivityApis/connectivity.api';

import { useConsultationSocket } from '../../connectivity/chat/ConsultationSocketProvider';
import { useExpertSocket } from '../../connectivity/chat/ExpertSocketProvider';

// REDUX IMPORTS
import { useDispatch } from 'react-redux';
import { clearActiveSession } from '../../store/slices/activeSessionSlice';

export const useVideo = () => {
  const [isStarting, setIsStarting] = useState(false);

  const startVideo = async (
    expertCode: string,
    categoryId: number | null,
    intakeData?: any,
  ) => {
    try {
      setIsStarting(true);

      const data =
        await videoConnectivityAPI.initiateVideoSession({
          expertCode,
          categoryId,
          intakeData,
        });

      return data;
    } catch (error: any) {
      const msg =
        error.response?.data?.message ||
        'Failed to start video call. Please check your balance.';

      toast.error(msg);
      return null;
    } finally {
      setIsStarting(false);
    }
  };

  return { startVideo, isStarting };
};

interface UseActiveVideoProps {
  sessionId: string;
  expertCode: string;
  isMeTheExpert: boolean;
  localExpertCode: string | null;
  rtcToken?: string;
  channelName?: string;
  sessionContext: any;
}

export const useActiveVideo = ({
  sessionId,
  expertCode,
  isMeTheExpert,
  localExpertCode,
  rtcToken,
  channelName,
  sessionContext,
}: UseActiveVideoProps) => {
  const dispatch = useDispatch();

  /*
   * Both providers remain mounted above the consultation
   * and dashboard routes.
   *
   * Select the appropriate existing connection rather than
   * creating a new Socket.IO connection inside VideoRoom.
   */
  const { socket: consultationSocket } = useConsultationSocket();
  const { socket: expertSocket } = useExpertSocket();

  const socket: Socket | null = isMeTheExpert
    ? expertSocket
    : consultationSocket;

  const [kundliImage, setKundliImage] = useState<string | null>(null);
  const [isGeneratingKundli, setIsGeneratingKundli] =
    useState(false);
  const [hasAutoGenerated, setHasAutoGenerated] =
    useState(false);
  const [fullScreenImage, setFullScreenImage] =
    useState<string | null>(null);

  const [sharedAttachments, setSharedAttachments] =
    useState<string[]>([]);
  const [isUploadingFile, setIsUploadingFile] =
    useState(false);

  /*
   * Agora credentials belong to the media layer.
   *
   * They must NOT control the lifecycle of the Socket.IO
   * consultation connection.
   *
   * Keep the latest values in refs so ping_expert always
   * receives the current Agora credentials without recreating
   * the Socket.IO connection.
   */
  const rtcTokenRef = useRef(rtcToken);
  const channelNameRef = useRef(channelName);

  useEffect(() => {
    rtcTokenRef.current = rtcToken;
    channelNameRef.current = channelName;
  }, [rtcToken, channelName]);

  // Safely check if it's a doctor straight from the intake context
  const isDoctorCategory =
    sessionContext?.intake?.categoryId === 3 ||
    sessionContext?.intake?.categoryName?.toLowerCase() ===
      'doctor' ||
    sessionContext?.intake?.categoryName?.toLowerCase() ===
      'medical';

  /*
   * Join the consultation using the persistent socket.
   *
   * The connect listener also runs after a temporary network
   * interruption, allowing the backend to cancel an active
   * grace period when the participant rejoins.
   *
   * IMPORTANT:
   * Do not disconnect this socket when VideoRoom unmounts.
   * Its lifecycle belongs to the persistent provider.
   */
  useEffect(() => {
    if (!socket || !sessionId) return;

    const joinConsultation = () => {
      /*
       * Join the backend consultation session.
       */
      socket.emit('join_session', {
        sessionId,
      });

      /*
       * Only the user pings the expert.
       *
       * Agora credentials are read from refs so their latest
       * values can be used without recreating this Socket.IO
       * connection.
       */
      if (!isMeTheExpert && expertCode) {
        socket.emit('ping_expert', {
          expertCode,
          sessionId,
          rtcToken: rtcTokenRef.current,
          channelName: channelNameRef.current,
        });
      }
    };

    socket.on('connect', joinConsultation);

    /*
     * The provider may already have connected before VideoRoom
     * mounted, so join immediately in that case.
     */
    if (socket.connected) {
      joinConsultation();
    }

    return () => {
      /*
       * Remove only this room's listener.
       *
       * The provider owns the actual connection and must keep
       * it alive while the video call is minimized.
       */
      socket.off('connect', joinConsultation);
    };
  }, [
    socket,
    sessionId,
    expertCode,
    isMeTheExpert,
  ]);

  /*
   * Receive consultation attachments and Kundli images.
   *
   * Clean up only this listener when leaving VideoRoom.
   * Never disconnect the shared socket.
   */
  useEffect(() => {
    if (!socket) return;

    const handleReceiveMessage = (msg: any) => {
      if (typeof msg?.message !== 'string') return;

      if (
        msg.message.includes('.svg') ||
        msg.message.includes('/astrology/')
      ) {
        setKundliImage(msg.message);
      } else if (
        msg.message.includes('res.cloudinary.com')
      ) {
        setSharedAttachments(prev => {
          if (prev.includes(msg.message)) {
            return prev;
          }

          return [...prev, msg.message];
        });
      }
    };

    socket.on('receive_message', handleReceiveMessage);

    return () => {
      socket.off('receive_message', handleReceiveMessage);
    };
  }, [socket]);

  // Passed isDoctorCategory so the timer hook counts up for the expert.
  const {
    timeLeft,
    formattedTime,
    endReasonText,
    setEndReasonText,
    isTimerHidden,
  } = useSessionTimer(
    socket,
    isMeTheExpert,
    isDoctorCategory,
  );

  const promoWarningShownRef = useRef(false);
  const tenSecWarningRef = useRef(false);

  useEffect(() => {
    if (timeLeft == null) return;

    if (
      !isMeTheExpert &&
      timeLeft <= 20 &&
      !promoWarningShownRef.current
    ) {
      promoWarningShownRef.current = true;

      toast.warning(
        '🔥 Expert is giving important insight… Recharge now to continue',
      );
    }

    if (
      !isMeTheExpert &&
      timeLeft === 10 &&
      !tenSecWarningRef.current
    ) {
      tenSecWarningRef.current = true;

      toast.error(
        '⏳ Session ending soon! Recharge now',
      );
    }
  }, [timeLeft, isMeTheExpert]);

  /*
   * Clear Redux session if the call ends for any reason.
   */
  useEffect(() => {
    if (endReasonText) {
      dispatch(clearActiveSession());
    }
  }, [endReasonText, dispatch]);

  const handleGenerateKundli = useCallback(
    async (
      style: 'north' | 'south' | 'east' = 'north',
    ) => {
      if (
        !socket ||
        isGeneratingKundli ||
        kundliImage
      ) {
        return;
      }

      setIsGeneratingKundli(true);

      try {
        const response =
          await astrologyAPI.generateKundliChart(
            sessionId,
            style,
          );

        const finalUrl =
          response.url ||
          response.chartUrl ||
          response.data?.url;

        if (finalUrl) {
          setKundliImage(finalUrl);

          socket.emit('send_message', {
            sessionId,
            senderType: 'expert',
            senderCode: localExpertCode,
            message: finalUrl,
          });
        } else {
          throw new Error('Invalid URL returned');
        }
      } catch (error) {
        toast.error(
          'Failed to generate Kundli chart.',
        );
      } finally {
        setIsGeneratingKundli(false);
      }
    },
    [
      socket,
      isGeneratingKundli,
      kundliImage,
      sessionId,
      localExpertCode,
    ],
  );

  useEffect(() => {
    const categoryName =
      sessionContext?.intake?.categoryName?.toLowerCase() ||
      '';

    const isAstrologySession =
      categoryName === 'astrology' ||
      categoryName === 'astrologer';

    const hasTob =
      !!sessionContext?.intake?.dynamicFormData?.tob;

    if (
      isMeTheExpert &&
      isAstrologySession &&
      hasTob &&
      !kundliImage &&
      !hasAutoGenerated &&
      !isGeneratingKundli
    ) {
      const timer = setTimeout(() => {
        handleGenerateKundli('north');
        setHasAutoGenerated(true);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [
    isMeTheExpert,
    sessionContext,
    kundliImage,
    hasAutoGenerated,
    isGeneratingKundli,
    handleGenerateKundli,
  ]);

  const handleFileUpload = async (file: File) => {
    if (!socket || !sessionId) {
      return;
    }

    setIsUploadingFile(true);

    try {
      const response =
        await connectivityAPI.uploadFile(
          file,
          sessionId,
        );

      if (response?.url) {
        setSharedAttachments(prev => [
          ...prev,
          response.url,
        ]);

        socket.emit('send_message', {
          sessionId,
          senderType: isMeTheExpert
            ? 'expert'
            : 'user',
          senderCode: isMeTheExpert
            ? localExpertCode || ''
            : localStorage.getItem('user_code') || '',
          message: response.url,
        });

        toast.success(
          'Attachment shared successfully!',
        );
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(
        'Failed to share attachment.',
      );
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handleEndSession = () => {
    if (
      window.confirm(
        'Are you sure you want to end this video call?',
      )
    ) {
      socket?.emit('end_session_manual', {
        sessionId,
        role: isMeTheExpert
          ? 'expert'
          : 'user',
      });

      setEndReasonText(
        'You ended the video call.',
      );

      dispatch(clearActiveSession());
    }
  };

  return {
    kundliImage,
    isGeneratingKundli,
    fullScreenImage,
    setFullScreenImage,

    timeLeft,
    formattedTime,
    endReasonText,
    isTimerHidden,

    handleGenerateKundli,
    handleEndSession,

    sharedAttachments,
    isUploadingFile,
    handleFileUpload,
  };
};